import React, { MutableRefObject, useCallback, useMemo, useState } from 'react'
import { ColDef, ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { PrimaryButton, Switch, Typography } from '@musinsa/layer-components-desktop'
import { format, parseISO } from 'date-fns'
import { ko } from 'date-fns/locale/ko'
import GridTableV2 from '@/components/tableV2/GridTableV2'
import { BundleConditionType, SingleBatchSettingItem } from '@/services/outbound/singleBatchSetting/types'
import { useExportBundleConditionsExcel } from '@/services/outbound/singleBatchSetting/useSingleBatchSettingQueries'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'
import { usePagination } from '@/hooks/usePagination'
import { ExportButton, Pagination } from '@/components/tableV2/GridTableV2Toolbar'
import { HStack } from '@musinsa/mwms-ds'
import { TruncatedListCellRenderer } from '@/components/tableV2/renderers/TruncatedListCellRenderer'
import ExcelDownloadModal from '@/components/table/ExcelDownloadModal'
import useModal from '@/hooks/useModal'
import { ExcelDownloadType } from '@/services/excel/excel.types'

// 전체센터 타입은 목록 API 가 빼고 내려준다 (BundleConditionRepositoryImpl 의 notIn(ALL_CENTER_TYPES)). Record 를 채우려고 둔 항목이다
const CONDITION_TYPE_LABEL: Record<BundleConditionType, string> = {
  CENTER_ALL: '전체상품',
  CENTER_SKU: 'SKU ID',
  CENTER_BARCODE: '바코드',
  ALL_CENTER_BARCODE: 'ALL_CENTER_BARCODE',
  ALL_CENTER_SKU: 'ALL_CENTER_SKU',
}

interface StatusCellRendererProps extends ICellRendererParams<SingleBatchSettingItem> {
  onStatusChange: (row: SingleBatchSettingItem) => void
  isStatusUpdating: boolean
}

function StatusCellRenderer({ data, onStatusChange, isStatusUpdating }: StatusCellRendererProps) {
  if (!data) return null
  return <Switch checked={data.status === 'ACTIVE'} disabled={isStatusUpdating} onCheckedChange={() => onStatusChange(data)} />
}

interface DateCellRendererProps extends ICellRendererParams<SingleBatchSettingItem> {
  onPeriodEdit: (row: SingleBatchSettingItem) => void
}

function DateCellRenderer({ data, value, onPeriodEdit }: DateCellRendererProps) {
  if (!data || !value) return null
  return (
    <button
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'var(--textButton-primary-fg)',
        textDecoration: 'underline',
        font: 'inherit',
        fontWeight: 500,
      }}
      onClick={() => onPeriodEdit(data)}
    >
      {format(parseISO(value as string), 'yyyy.MM.dd HH:mm')}
    </button>
  )
}

interface SingleBatchSettingTableProps {
  gridRef: MutableRefObject<AgGridReact | null>
  items: SingleBatchSettingItem[]
  totalCount: number
  isLoading?: boolean
  currentPage: number
  countPerPage: number
  onChangePage: (page: number) => void
  onCountPerPageChange: (size: number) => void
  onDelete: (rows: SingleBatchSettingItem[]) => void
  onPeriodEdit: (row: SingleBatchSettingItem) => void
  onStatusChange: (row: SingleBatchSettingItem) => void
  isStatusUpdating: boolean
}

export function SingleBatchSettingTable({
  gridRef,
  items,
  totalCount,
  isLoading,
  currentPage,
  countPerPage,
  onChangePage,
  onCountPerPageChange,
  onDelete,
  onPeriodEdit,
  onStatusChange,
  isStatusUpdating,
}: SingleBatchSettingTableProps) {
  const [selectedRows, setSelectedRows] = useState<SingleBatchSettingItem[]>([])
  const selectedCount = selectedRows.length
  const { isLoading: isExporting, onExportData } = useExportBundleConditionsExcel()
  const { options: warehouseOptions } = useGetCutOffWarehouses()
  const warehouseNameMap = useMemo(() => new Map(warehouseOptions.map((o) => [o.value, o.label])), [warehouseOptions])

  const exportModal = useModal()

  const handleExportExcel = useCallback(
    (reason?: ExcelDownloadType) => {
      if (selectedRows[0] && reason) onExportData(selectedRows[0].id, reason)
    },
    [selectedRows, onExportData],
  )

  const paginationProps = usePagination({
    totalCount,
    currentPage,
    pageSize: countPerPage,
    onChangePage,
    onPageSizeChange: onCountPerPageChange,
  })

  const columnDefs = useMemo<ColDef<SingleBatchSettingItem>[]>(
    () => [
      {
        field: 'warehouseCode',
        headerName: '적용센터',
        width: 140,
        valueFormatter: ({ value }) => (value ? warehouseNameMap.get(value) ?? value : ''),
      },
      {
        field: 'conditionType',
        headerName: '조건 타입',
        width: 120,
        valueFormatter: ({ data }) => (data ? CONDITION_TYPE_LABEL[data.conditionType] ?? data.conditionType : ''),
      },
      {
        field: 'status',
        headerName: '적용여부',
        width: 100,
        cellRenderer: StatusCellRenderer,
        cellRendererParams: { onStatusChange, isStatusUpdating },
      },
      {
        field: 'startAt',
        headerName: '시작일시',
        width: 200,
        cellRenderer: DateCellRenderer,
        cellRendererParams: { onPeriodEdit },
      },
      {
        field: 'endAt',
        headerName: '종료일시',
        width: 200,
        cellRenderer: DateCellRenderer,
        cellRendererParams: { onPeriodEdit },
      },
      {
        field: 'barcodeCount',
        headerName: '등록 건수',
        width: 150,
        valueFormatter: ({ value }) => (value == null ? '-' : String(value)),
      },
      {
        field: 'barcodes',
        headerName: '등록 상품',
        width: 300,
        minWidth: 300,
        cellRenderer: TruncatedListCellRenderer,
        cellRendererParams: {
          getItems: (data: SingleBatchSettingItem) => data.barcodes,
          getTotalCount: (data: SingleBatchSettingItem) => data.barcodeCount ?? data.barcodes.length,
          getOverrideText: (data: SingleBatchSettingItem) => (data.conditionType === 'CENTER_ALL' ? '전체상품' : null),
        },
      },
      {
        field: 'createdAt',
        headerName: '생성일시',
        width: 200,
        valueFormatter: ({ value }) => (value ? format(parseISO(value as string), 'yyyy.MM.dd (EEE) HH:mm:ss', { locale: ko }) : ''),
      },
      { field: 'createdBy', headerName: '생성자', width: 160 },
    ],
    [onPeriodEdit, onStatusChange, isStatusUpdating, warehouseNameMap],
  )

  const handleSelectionChanged = (e: SelectionChangedEvent<SingleBatchSettingItem>) => {
    setSelectedRows(e.api.getSelectedRows())
  }

  return (
    <>
      <GridTableV2<SingleBatchSettingItem>
        gridKey="GI36_HEAD"
        ref={gridRef}
        idKey="id"
        rowData={items}
        isLoading={isLoading}
        columnDefs={columnDefs}
        totalCount={totalCount}
        rowStartIndex={currentPage * countPerPage}
        rowSelectable
        useColumnNo
        showTableHeader={false}
        onSelectionChanged={handleSelectionChanged}
        noRowsOverlayComponentParams={{
          mainContent: '등록된 단수합포 설정이 없습니다.',
          subContent: '단수합포 설정을 등록해 주세요.',
        }}
        title={
          <>
            <HStack gap={12} align="center">
              <HStack gap={8}>
                <HStack gap={4}>
                  <Typography name="base-text-m" colorToken="fg">
                    전체
                  </Typography>
                  <Typography name="base-text-m-semibold" colorToken="fg">
                    {totalCount}
                  </Typography>
                </HStack>
                {selectedCount > 0 && (
                  <>
                    <Typography name="base-text-m" colorToken="fg-lowest">
                      ·
                    </Typography>
                    <HStack gap={4}>
                      <Typography name="base-text-m" colorToken="fg">
                        선택
                      </Typography>
                      <Typography name="base-text-l-semibold" colorToken="layerDesktop-fg-accent">
                        {selectedCount}
                      </Typography>
                    </HStack>
                  </>
                )}
              </HStack>
              <PrimaryButton variant="outlined" size="small" label="삭제" disabled={selectedCount === 0} onClick={() => onDelete(selectedRows)} />
            </HStack>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-end' }}>
              <ExportButton
                label="엑셀 다운로드"
                disabled={selectedCount !== 1 || isExporting || selectedRows[0]?.conditionType === 'CENTER_ALL'}
                onClick={exportModal.onOpenModal}
              />
              <Pagination {...paginationProps} />
            </div>
          </>
        }
      />
      {exportModal.open && (
        <ExcelDownloadModal open withPassword={false} serverAudit onConfirm={handleExportExcel} onClose={exportModal.onCloseModal} />
      )}
    </>
  )
}
